import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import CallexLoader from '../../../components/CallexLoader';
import Pagination from '../../../components/form/Pagination';
import { checkModulePermission } from '../../../utils/common';
import AdminHeader from '../../../components/header/AdminHeader';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';

import {
  useCallexAiAgentList,
  callexAiAgentDelete,
  callexAiAgentListGet
} from '../../../store/slices/tenantSlices/configurationSlice';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import {
  LANGUAGE_OPTIONS,
  SPEAKING_STYLE_OPTIONS,
  STATUS_OPTIONS
} from './AddEditCallexAiAgent';
import TestCallModal from './TestCallModal';


const CallexAiAgents = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const callexAiAgentList = useCallexAiAgentList();
  const userRolePermission = useUserRolePermission();

  const [isDeleteId, setIsDeleteId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [testCallAgentId, setTestCallAgentId] = useState(null);
  const [isTestCallModalOpen, setIsTestCallModalOpen] = useState(false);


  const fetchCallexAiAgentList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter };
      await dispatch(callexAiAgentListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  const deleteCallexAiAgent = async () => {
    setIsDeleting(true);
    try {
      const response = await dispatch(callexAiAgentDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        if (callexAiAgentList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchCallexAiAgentList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleTestCallModal = (agentId = null) => {
    setTestCallAgentId(agentId);
    setIsTestCallModalOpen(!isTestCallModalOpen);
  };

  useEffect(() => {
    fetchCallexAiAgentList();
  }, [filter]);


  return (
    <div>
      <AdminHeader
        title="AI Agents"
        isExpandSearch
        isAdd
        onAddClick={() => navigate('/ai-callex/ai/callex-ai-agents/new')}
        isAddDisabled={!checkModulePermission('is_add', 'AI Agents', userRolePermission)}
        addBtnName="Add AI Agent"
        searchPlaceholder="Search AI Agents"
        search={filter.search}
        handelSearch={(e) => setFilter({ ...filter, search: e.target.value })}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : callexAiAgentList?.data?.length > 0 &&
        checkModulePermission('is_view', 'AI Agents', userRolePermission) ? (
        <div className="p-0 position-relative m-0">
          <div
            style={{
              height: 'calc(100vh - 210px)',
              overflowY: 'auto'
            }}>
            <div
              className="d-grid gap-3 py-2"
              style={{
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: '20px'
              }}>
              {callexAiAgentList?.data?.map((agent) => (
                <Card key={agent.id} className="ai-agent-card card-sm">
                  {/* Header */}
                  <div className="ai-agent-card__header">
                    <h5 className="ai-agent-card__title">{agent.name}</h5>

                    <div className="ai-agent-card__actions">
                      <Icon.Phone
                        size={16}
                        className="cursor-pointer text-success"
                        onClick={() => toggleTestCallModal(agent.id)}
                      />
                      <Icon.Edit
                        size={16}
                        className={`cursor-pointer text-info ${!checkModulePermission('is_view', 'AI Agents', userRolePermission) &&
                          'disabled'
                          }`}
                        onClick={() => navigate(`/ai-callex/ai/callex-ai-agents/${agent.id}/edit`)}
                      />
                      <Icon.Trash
                        size={16}
                        className={`cursor-pointer text-danger ${!checkModulePermission('is_delete', 'AI Agents', userRolePermission) &&
                          'disabled'
                          }`}
                        onClick={() => setIsDeleteId(agent.id)}
                      />
                    </div>

                  </div>

                  {/* Body */}
                  <div className="ai-agent-card__body">
                    <div className="agent-row">
                      <span>Speaking Style:</span>
                      <strong>
                        {SPEAKING_STYLE_OPTIONS.find((opt) => opt.value === agent.speakingStyle)
                          ?.label || agent.speakingStyle || 'Standard'}
                      </strong>
                    </div>

                    <div className="agent-row">
                      <span>Temp / Tokens:</span>
                      <strong>
                        {agent.temperature} / {agent.maxTokens}
                      </strong>
                    </div>

                    <div className="agent-row">
                      <span>Language:</span>
                      <strong>
                        {LANGUAGE_OPTIONS.find((opt) => opt.value === agent.language)?.label ||
                          agent.language}
                      </strong>
                    </div>

                    <div className="agent-row">
                      <span>Max Duration:</span>
                      <strong>{agent.maxDuration} min</strong>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="ai-agent-card__footer">
                    <div className="ai-agent-card__tags">
                      <span className={`status-pill ${agent.status}`}>
                        {STATUS_OPTIONS.find((opt) => opt.value === agent.status)?.label ||
                          agent.status}
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Pagination */}
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={callexAiAgentList?.total}
              limit={callexAiAgentList?.limit}
              page={callexAiAgentList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchCallexAiAgentList()}
            />
          </div>
        </div>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteCallexAiAgent()}
        isLoading={isDeleting}
      />

      <TestCallModal
        isOpen={isTestCallModalOpen}
        toggle={() => toggleTestCallModal()}
        agentId={testCallAgentId}
      />
    </div>

  );
};

export default CallexAiAgents;
